import React, {useEffect} from "react"
import {isUndefined} from "lodash"
import moment from "moment"
import {
	Editable,
	EditableAddress,
	EditableDate,
	EditableDropDownField,
	EditableFullName,
	EditableHiddenString,
	EditablePhone,
	OptionalEditable,
} from "../HorizontalField/EditableHorizontalField"
import IdentificationNumber from "../IdentificationNumber/IdentificationNumber"
import {Accordion} from "../Accordion/Accordion"
import {EditableOccupationField} from "../ApplicationAdditionalInformationFields/OccupationField"
import {EditableAnnualIncomeField} from "../ApplicationAdditionalInformationFields/AnnualIncomeField"
import {EditableSourceOfIncomeField} from "../ApplicationAdditionalInformationFields/SourceOfIncomeField"
import {emailPreview} from "../InformationHiding/InformationHiding"
import {hasAdditionalInformation, OfficerTitle} from "../../resources/application"
import {Address, FullName, Permissible, Phone} from "../../resources/common"
import {AnnualIncome, Occupation, OfficerTitleMapping, SourceOfIncome} from "../../resources/application"
import {CustomerOfficer as CustomerOfficerResource, patchCustomerOfficer} from "../../resources/customerOfficer"
import {SSNProps} from "../IdentificationNumber/IdentificationNumber"
import {useAccessToken, useIsUnitUser} from "../../services/auth"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useToasts} from "react-toast-notifications"

type CustomerOfficerAttributes = CustomerOfficerResource["attributes"]

type CustomerOfficerProps = {
	officerAttributes: CustomerOfficerAttributes
	customerId: string
	officerId: string
	isAllowed: boolean
	officerDocumentTitle: string | undefined
	officerName: FullName | undefined
	setOfficerName: (value: FullName | undefined) => void
	officerTitle: OfficerTitle | undefined
	setOfficerTitle: (value: OfficerTitle | undefined) => void
	officerDob: Date | undefined
	setOfficerDob: (value: Date | undefined) => void
	officerPhone: Permissible<Phone>
	setOfficerPhone: (value: Permissible<Phone>) => void
	officerEmail: Permissible<string>
	setOfficerEmail: (value: Permissible<string>) => void
	officerAddress: Permissible<Address>
	setOfficerAddress: (value: Permissible<Address>) => void
	officerOccupation: Occupation | undefined
	setOfficerOccupation: (value: Occupation | undefined) => void
	officerAnnualIncome: AnnualIncome | undefined
	setOfficerAnnualIncome: (value: AnnualIncome | undefined) => void
	officerSourceOfIncome: SourceOfIncome | undefined
	setOfficerSourceOfIncome: (value: SourceOfIncome | undefined) => void
	isEditing: string | undefined
	setIsEditing: (value: string | undefined) => void
	isUpdating: boolean
	setIsUpdating: (value: boolean) => void
	isHiddenIconVisible: boolean
	refresh: () => void
}

export function CustomerOfficer({
	officerAttributes,
	customerId,
	officerId,
	isAllowed,
	officerDocumentTitle,
	officerName,
	setOfficerName,
	officerTitle,
	setOfficerTitle,
	officerDob,
	setOfficerDob,
	officerPhone,
	setOfficerPhone,
	officerEmail,
	setOfficerEmail,
	officerAddress,
	setOfficerAddress,
	officerOccupation,
	setOfficerOccupation,
	officerAnnualIncome,
	setOfficerAnnualIncome,
	officerSourceOfIncome,
	setOfficerSourceOfIncome,
	isEditing,
	setIsEditing,
	isUpdating,
	setIsUpdating,
	isHiddenIconVisible,
	refresh,
}: CustomerOfficerProps) {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const [updateOfficerState, patchOfficer] = useAsyncResultIdle(patchCustomerOfficer)

	useEffect(() => {
		if (updateOfficerState.isOk()) {
			addToast("Customer Updated Successfully", {appearance: "success"})
			setIsUpdating(false)
			refresh()
		} else if (updateOfficerState.isErr()) {
			addToast(updateOfficerState.error.errors[0].detail || updateOfficerState.error.errors[0].title, {
				appearance: "error",
			})
			setIsUpdating(false)
			refresh()
		}
	}, [updateOfficerState])

	const isUnitUser = useIsUnitUser()

	return (
		<div className="card">
			<div className="card-header">
				<p className="card-header-title">Officer Information</p>
			</div>
			<div className="card-content">
				{officerName && (
					<Editable
						label="Name"
						isEditing={isEditing === "Officer Name"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed && isUnitUser}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("Officer Name")}
						onCancel={refresh}
						setValue={setOfficerName}
						value={officerName}
						initial={officerName}
						update={() => patchOfficer(accessToken, customerId, officerId, {fullName: officerName})}
						className={""}
					>
						{EditableFullName}
					</Editable>
				)}

				{officerTitle && (
					<Editable
						label="Title"
						isEditing={isEditing === "Officer Title"}
						isDisabled={!isUndefined(isEditing)}
						isAllowed={isAllowed && isUnitUser}
						isUpdating={isUpdating}
						onStartEdit={() => setIsEditing("Officer Title")}
						onCancel={refresh}
						setValue={setOfficerTitle}
						value={officerTitle}
						initial={officerTitle}
						update={() => {
							patchOfficer(accessToken, customerId, officerId, {title: officerTitle})
							setIsUpdating(true)
						}}
						className={""}
						possibleValues={OfficerTitleMapping}
					>
						{EditableDropDownField}
					</Editable>
				)}
				<IdentificationNumber
					data={{
						initialSsn: {
							ssn: officerAttributes.ssn ?? officerAttributes.maskedSSN ?? "",
							nationality: officerAttributes.nationality ?? "",
						},
						initialPassport: [
							officerAttributes.nationality ?? "",
							officerAttributes.passport ?? officerAttributes.maskedPassport ?? "",
						],
						initialMatriculaConsular:
							officerAttributes.matriculaConsular ?? officerAttributes.maskedMatriculaConsular ?? "",

						documentTitle: officerDocumentTitle,
						archived: false,
					}}
					state={{
						isEditing,
						isEditable: isAllowed && isUnitUser,
						isUpdating,
						isHiddenIconVisible,
					}}
					actions={{
						setIsEditing,
						refresh,
						updateSSN: (ssnProps: SSNProps) =>
							patchOfficer(accessToken, customerId, officerId, {ssn: ssnProps.ssn, nationality: ssnProps.nationality}),
						updatePassport: (passport: [string, string]) =>
							patchOfficer(accessToken, customerId, officerId, {passport: passport[1], nationality: passport[0]}),
						updateMatriculaConsular: (matriculaConsular: string) =>
							patchOfficer(accessToken, customerId, officerId, {matriculaConsular}),
						setIsUpdating,
					}}
				/>
				<OptionalEditable
					label="Date Of Birth"
					isEditing={isEditing === "Officer Birth Date"}
					isDisabled={!isUndefined(isEditing)}
					isAllowed={isAllowed && isUnitUser}
					isUpdating={isUpdating}
					isHiddenIconVisible={isHiddenIconVisible}
					onStartEdit={() => setIsEditing("Officer Birth Date")}
					onCancel={refresh}
					setValue={setOfficerDob}
					value={officerDob}
					initial={officerDob}
					update={() => {
						patchOfficer(accessToken, customerId, officerId, {
							dateOfBirth: officerDob ? moment(officerDob).format("YYYY-MM-DD") : undefined,
						})
						setIsUpdating(true)
					}}
					className={""}
				>
					{EditableDate}
				</OptionalEditable>
				<OptionalEditable
					label="Phone"
					isEditing={isEditing === "Officer Phone"}
					isDisabled={!isUndefined(isEditing)}
					isAllowed={isAllowed && isUnitUser}
					isUpdating={isUpdating}
					isHiddenIconVisible={isHiddenIconVisible}
					onStartEdit={() => setIsEditing("Officer Phone")}
					onCancel={refresh}
					setValue={setOfficerPhone}
					value={officerPhone}
					initial={officerPhone}
					update={() => {
						patchOfficer(accessToken, customerId, officerId, {phone: officerPhone})
						setIsUpdating(true)
					}}
					className={""}
				>
					{EditablePhone}
				</OptionalEditable>
				<OptionalEditable
					label="Email"
					isEditing={isEditing === "Officer Email"}
					isDisabled={!isUndefined(isEditing)}
					isAllowed={isAllowed && isUnitUser}
					isUpdating={isUpdating}
					isHiddenIconVisible={isHiddenIconVisible}
					onStartEdit={() => setIsEditing("Officer Email")}
					onCancel={refresh}
					setValue={setOfficerEmail}
					value={officerEmail}
					initial={officerEmail}
					update={() => {
						patchOfficer(accessToken, customerId, officerId, {email: officerEmail})
						setIsUpdating(true)
					}}
					inputType="email"
					previewFunction={emailPreview}
					className={""}
				>
					{EditableHiddenString}
				</OptionalEditable>
				<OptionalEditable
					label="Address"
					isEditing={isEditing === "Officer Address"}
					isDisabled={!isUndefined(isEditing)}
					isAllowed={isAllowed && isUnitUser}
					isUpdating={isUpdating}
					onStartEdit={() => setIsEditing("Officer Address")}
					onCancel={refresh}
					setValue={setOfficerAddress}
					value={officerAddress}
					initial={officerAddress}
					update={() => {
						patchOfficer(accessToken, customerId, officerId, {address: officerAddress})
						setIsUpdating(true)
					}}
					className={""}
				>
					{EditableAddress}
				</OptionalEditable>
				{hasAdditionalInformation(officerAttributes) ? (
					<Accordion title={"Additional Information"} theme={"light"} noContentPadding={true}>
						<div>
							<EditableOccupationField
								occupation={officerOccupation}
								setOccupation={setOfficerOccupation}
								isEditing={isEditing === "Occupation"}
								isDisabled={!isUndefined(isEditing)}
								isAllowed={isAllowed}
								onStartEdit={() => setIsEditing("Occupation")}
								onCancel={refresh}
								update={() => {
									patchOfficer(accessToken, customerId, officerId, {occupation: officerOccupation})
									setIsUpdating(true)
								}}
							/>
							<EditableAnnualIncomeField
								annualIncome={officerAnnualIncome}
								setAnnualIncome={setOfficerAnnualIncome}
								isEditing={isEditing === "OfficerAnnualIncome"}
								isDisabled={!isUndefined(isEditing)}
								isAllowed={isAllowed}
								onStartEdit={() => setIsEditing("OfficerAnnualIncome")}
								onCancel={refresh}
								update={() => {
									patchOfficer(accessToken, customerId, officerId, {annualIncome: officerAnnualIncome})
									setIsUpdating(true)
								}}
							/>

							<EditableSourceOfIncomeField
								sourceOfIncome={officerSourceOfIncome}
								setSourceOfIncome={setOfficerSourceOfIncome}
								isEditing={isEditing === "OfficerSourceOfIncome"}
								isDisabled={!isUndefined(isEditing)}
								isAllowed={isAllowed}
								onStartEdit={() => setIsEditing("OfficerSourceOfIncome")}
								onCancel={refresh}
								update={() => {
									patchOfficer(accessToken, customerId, officerId, {sourceOfIncome: officerSourceOfIncome})
									setIsUpdating(true)
								}}
							/>
						</div>
					</Accordion>
				) : null}
			</div>
		</div>
	)
}
